import React, { useEffect, useState } from "react";
import { CreateCompanyData } from "../../store/types";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { validateField, formatPhone } from "../../hooks/validation";
import { useGetQuery } from "../../store/slices/apiSlice";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomInputMask from "../../components/CustomInput/CustomInputMask";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import {
  IconArrowLeft,
  IconCompany,
  IconMail,
  IconPhone,
  IconCompanyFilled,
  IconMailFilled,
  IconPhoneFilled,
} from "../../assets/icons/Icon";
import { H2, DangerText } from "../../styles/Typography";
import { Container } from "../../components/Container/Container";
import {
  Flex,
  ButtonLabel,
  GoBackButton,
  ButtonContainer,
  Spinner,
} from "./CRUDPages.styles";

const CompanyFormPage: React.FC = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get("mode") || "create";
  const companyId = searchParams.get("id");
  const [loading, setLoading] = useState(false);

  const {
    companiesEP,
    handlePostCompany,
    handleUpdateCompany,
    handleDeleteCompany,
    refetchData,
    toastRef,
  } = useCompaniesAPI();

  const [formData, setFormData] = useState<CreateCompanyData>({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState<
    Record<keyof CreateCompanyData, boolean>
  >({
    name: false,
    email: false,
    phone: false,
  });
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);

  const {
    data: company,
    refetch: refetchCompany,
    isLoading,
  } = useGetQuery(`${companiesEP}/${companyId}`, {
    refetchOnMountOrArgChange: true,
    skip: !companyId,
  });

  useEffect(() => {
    if (mode !== "create" && isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [company, isLoading]);

  useEffect(() => {
    if (mode !== "create" && companyId && company) {
      const newData = {
        name: company.name || "",
        email: company.email || "",
        phone: company.phone || "",
      };
      setFormData(newData);
      const newErrors: Record<keyof CreateCompanyData, boolean> = {
        name: !validateField("name", newData.name),
        email: !validateField("email", newData.email),
        phone: !validateField("phone", newData.phone),
      };
      setErrors(newErrors);
    } else {
      setFormData({
        name: "",
        email: "",
        phone: "",
      });
      setErrors({
        name: false,
        email: false,
        phone: false,
      });
    }
  }, [companyId, mode, company]);

  useEffect(() => {
    if (isMobile === null) return;

    if (!isMobile) {
      navigate("/companies", { replace: true });
    }
  }, [isMobile, navigate]);

  const handleFieldChange = (field: keyof CreateCompanyData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    const newErrors: Record<keyof CreateCompanyData, boolean> = {
      name: !validateField("name", formData.name),
      email: !validateField("email", formData.email),
      phone: !validateField("phone", formData.phone),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateCompanyData) => {
    const value = formData[field];
    const isValid = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (mode !== "delete")
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }

    setValidForm(true);
    setSubmitting(true);
    let success = false;
    try {
      if (mode === "create") {
        success = await handlePostCompany(formData);
      } else if (mode === "edit") {
        success = await handleUpdateCompany({ ...formData, id: company!.id });
      } else if (mode === "delete") {
        success = await handleDeleteCompany(company!);
      }
      if (success) {
        refetchData();
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else return;
    } finally {
      setSubmitting(false);
    }
  };

  const getHeader = () => {
    switch (mode) {
      case "create":
        return "Новая компания";
      case "edit":
        return "Редактировать компанию";
      case "delete":
        return "Удалить компанию";
      default:
        return "Ошибка";
    }
  };

  const renderForm = () => (
    <>
      <CustomInputText
        value={formData.name}
        setValue={(val) => handleFieldChange("name", val)}
        onBlur={() => handleBlur("name")}
        placeholder="Имя"
        icon={<IconCompany size={20} />}
        invalid={errors.name}
      />
      <CustomInputText
        value={formData.email}
        setValue={(val) => handleFieldChange("email", val)}
        onBlur={() => handleBlur("email")}
        placeholder="Почта"
        icon={<IconMail size={20} />}
        invalid={errors.email}
      />
      <CustomInputMask
        value={formData.phone}
        setValue={(val) => handleFieldChange("phone", val)}
        onBlur={() => handleBlur("phone")}
        mask="+7 (999) 999-99-99"
        unmask={true}
        placeholder="+7 (___) ___-__-__"
        icon={<IconPhone size={20} />}
        invalid={errors.phone}
      />
    </>
  );

  const renderDelete = () => (
    <>
      <DangerText>Вы уверены, что хотите удалить данную компанию?</DangerText>
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={company?.name ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconMailFilled size={20} />}
        text={company?.email ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconPhoneFilled size={20} />}
        text={
          company
            ? formatPhone(company.phone) == ""
              ? "Нет данных"
              : formatPhone(company.phone)
            : "Нет данных"
        }
      />
    </>
  );

  if (loading)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <H2>{getHeader()}</H2>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {mode === "delete" ? renderDelete() : renderForm()}
      <Flex>
        <GoBackButton>
          <Button outlined onClick={() => navigate(-1)}>
            <ButtonLabel>
              <IconArrowLeft size={16} />
              Назад
            </ButtonLabel>
          </Button>
        </GoBackButton>
        <ButtonContainer>
          <Button
            label={
              mode === "delete"
                ? "Удалить"
                : mode === "edit"
                ? "Сохранить"
                : "Создать"
            }
            onClick={handleSubmit}
            loading={submitting}
          />
        </ButtonContainer>
      </Flex>
    </Container>
  );
};

export default CompanyFormPage;
